import * as React from 'react';
import { FC, useState, useEffect } from 'react';
import type { Game } from 'app/CasinoGames/types';
import type { Fullscreen } from 'app/CasinoGames/useFullscreen';
import { useLocales } from '../locales';
import { GameStarter } from '../useGameStarter';
import Favorites from '../Favorites';
import iconFullscreen from './images/icon_fullscreen.svg';
import iconFullscreenExit from './images/icon_fullscreen-exit.svg';
import iconChat from './images/icon_chat.svg';

// Zendesk's Web Widget (Classic). Called through window.zE every time: until the widget has loaded
// that is a stub queueing the calls, replaced by the real thing once it has.
const zE = (...args: unknown[]) => window.zE?.(...args);

// Whether a game is shown, for the widget's close handler: a handler can't be removed once added.
let gameShown = false;
let closeHandlerAdded = false;

// How long the button waits for the browser to report the switch before it stops showing the tap:
// a request the browser refuses (no user gesture, a blocked iframe) never reports at all.
const FULLSCREEN_SWITCH_TIMEOUT = 2000;
// The shortest the tap shows: a desktop browser can switch within a frame, too fast to be seen.
const FULLSCREEN_TAP_SHOWN_FOR = 300;

// The browser takes a moment to switch to fullscreen and back - long enough on a phone for a tap to
// look missed - so the button lights up with a spinner (aria-busy) from the tap until the switch
// is reported, and for at least FULLSCREEN_TAP_SHOWN_FOR.
const FullscreenButton: FC<{ fullscreen: Fullscreen }> = ({ fullscreen }) => {
  const [switching, setSwitching] = useState(false);
  const [tapShown, setTapShown] = useState(false);

  useEffect(() => setSwitching(false), [fullscreen.enabled]);

  useEffect(() => {
    if (!switching) return undefined;
    const timeout = setTimeout(() => setSwitching(false), FULLSCREEN_SWITCH_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [switching]);

  useEffect(() => {
    if (!tapShown) return undefined;
    const timeout = setTimeout(() => setTapShown(false), FULLSCREEN_TAP_SHOWN_FOR);
    return () => clearTimeout(timeout);
  }, [tapShown]);

  const toggle = () => {
    setSwitching(true);
    setTapShown(true);
    if (fullscreen.enabled) fullscreen.disable();
    else fullscreen.enable();
  };

  return (
    <button
      type="button"
      className="xo-icon-button xo-icon-button_fullscreen"
      onClick={toggle}
      aria-busy={switching || tapShown}
      aria-label="full screen"
      title="full screen"
    >
      <img src={fullscreen.enabled ? iconFullscreenExit : iconFullscreen} alt="" />
      <span className="xo-icon-button_fullscreen-spinner" aria-hidden="true" />
    </button>
  );
};

// Zendesk's launcher floats in the bottom-right corner, over the game or its controls, so while a game
// is shown it is hidden and this button opens the widget instead; closing the widget hides it again.
// A widget the visitor already has open is left open.
const ChatButton: FC<{ fullscreen: Fullscreen }> = ({ fullscreen }) => {
  const locales = useLocales();
  // Read after mount: there is no window during server rendering.
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!window.zE) return undefined;

    setAvailable(true);
    gameShown = true;
    // Nothing until the widget has loaded, and it loads closed.
    const display = zE('webWidget:get', 'display');
    if (!display || display === 'launcher') zE('webWidget', 'hide');
    if (!closeHandlerAdded) {
      closeHandlerAdded = true;
      zE('webWidget:on', 'close', () => gameShown && zE('webWidget', 'hide'));
    }
    return () => {
      gameShown = false;
      zE('webWidget', 'show');
    };
  }, []);

  if (!available) return null;

  const open = () => {
    // The widget opens outside the game, out of sight in fullscreen.
    if (fullscreen.enabled) fullscreen.disable();
    zE('webWidget', 'show');
    zE('webWidget', 'open');
  };

  return (
    <button
      type="button"
      className="xo-icon-button"
      onClick={open}
      aria-label={locales('chat')}
      title={locales('chat')}
    >
      <img src={iconChat} alt="" />
    </button>
  );
};

const ModeSwitch: FC<{ gameStarter: GameStarter; playerPresent: boolean }> = ({ gameStarter, playerPresent }) => {
  const locales = useLocales();

  if (!gameStarter.game?.demo) return null;

  const handleSwitchToReal = () => {
    if (!playerPresent) {
      window.location.assign('/login');
      return;
    }
    gameStarter.switchMode();
  };

  return (
    <div className="xo-game-mode">
      <button
        type="button"
        className="xo-game-mode_button xo-game-mode_button__fun"
        onClick={() => gameStarter.switchMode()}
        disabled={gameStarter.playMode === 'practice'}
      >
        {locales('fun')}
      </button>
      <button
        type="button"
        className="xo-game-mode_button xo-game-mode_button__real"
        onClick={handleSwitchToReal}
        disabled={gameStarter.playMode === 'real'}
      >
        {locales('real')}
      </button>
    </div>
  );
};

const Controls: FC<{
  gameStarter: GameStarter;
  fullscreen: Fullscreen;
  playerPresent: boolean;
}> = ({ gameStarter, fullscreen, playerPresent }) => (
  <div className="xo-game-controls">
    <ModeSwitch gameStarter={gameStarter} playerPresent={playerPresent} />
    <div className="xo-game-logo" />
    <div className="xo-game-player-actions">
      <ChatButton fullscreen={fullscreen} />
      {playerPresent && <Favorites game={gameStarter.game as Game} />}
      {fullscreen.available && <FullscreenButton fullscreen={fullscreen} />}
    </div>
  </div>
);

export default Controls;
